// @owners { team: patients-team }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Column,
  InputCheckbox,
  ListDescription,
  LoadingEmptyState,
  MdSpacing,
  SmSpacing,
  XlPadding,
} from '@alto/design-system';
import React, { useState } from 'react';
// eslint-disable-next-line import/no-deprecated
import { toggleSubscription, updateUserSubscriptions } from '~shared/actions/subscriptions';
import { getCompanyName } from '~shared/config/getCompanyName';
import { type SubscriptionType, subscriptionsConstants } from '~shared/constants';
import { getEmail, getSubscriptions } from '~shared/selectors/getSubscriptionsState';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { StayInTouch } from './StayInTouch';

type Props = {
  readonly setIsUpdated: (isUpdated: boolean) => void;
  readonly uuid: string;
};

export const SubscriptionOptions = ({ setIsUpdated, uuid }: Props) => {
  const dispatch = useDispatchShared();
  const email = useSelectorShared(getEmail);
  const subscriptions = useSelectorShared(getSubscriptions);
  const isLoading = useSelectorShared(
    (state) => state.ui.loading.fetchUserSubscriptionsLoading || state.ui.loading.updateUserSubscriptionsLoading,
  );

  const [isChanged, setIsChanged] = useState(false);

  const setSubscriptions = (subscription: string) => {
    dispatch(toggleSubscription(subscription));
    setIsChanged(true);
  };

  const handleUpdate = async (unSubscribeAll = false) => {
    // eslint-disable-next-line import/no-deprecated
    const success = await dispatch(updateUserSubscriptions(uuid, unSubscribeAll));
    setIsUpdated(success);
  };

  if (isLoading) {
    return <LoadingEmptyState />;
  }

  return (
    <>
      <StayInTouch email={email} />
      <XlPadding topPadding={SPACING.STATIC.NONE}>
        <Column gap="XS">
          {subscriptions.map(({ comms_type, subscribed }) => (
            <InputCheckbox
              noHighlight
              checkPlacement="left"
              name={comms_type}
              label={subscriptionsConstants[comms_type as SubscriptionType].category}
              descriptions={
                <ListDescription>{subscriptionsConstants[comms_type as SubscriptionType].description}</ListDescription>
              }
              key={comms_type}
              value={subscribed}
              onPress={() => {
                setSubscriptions(comms_type);
              }}
            />
          ))}
        </Column>
      </XlPadding>
      <Button
        disabled={!isChanged}
        label="Update"
        type="primary"
        onPress={() => handleUpdate()}
      />
      <SmSpacing />
      <Button
        label="Or unsubscribe all"
        onPress={() => {
          handleUpdate(true);
        }}
        type="tertiary"
      />
      <MdSpacing />
      <Body
        center
        fontFamily="semibold"
        textSize="detail"
        color={COLORS.PALETTE.GREYSCALE.DARKER}
      >
        <strong>Please note:</strong> you may still receive account-related emails from ${getCompanyName()} related to
        the management and delivery of your medications.
      </Body>
    </>
  );
};
